import {
  Avatar,
  Button,
  FlexBox,
  Icon,
  IconButton,
  InputText,
  SelectionList,
  Spinner,
  Text,
} from '@dixa/lib-shared-habla';
import * as Popover from '@radix-ui/react-popover';
import { useCallback, useRef, useState } from 'react';
import { Waypoint } from 'react-waypoint';

import { searchApi } from 'apis/search';
import { transformSearchUserToUser } from 'apis/users/utils/transformUser/transformUser';
import { actions as personalActions } from 'components/settings/actions/personal';
import { useLazyPaginatedEndpoint } from 'utils/apis/pagination/useLazyPaginatedEndpoint';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { bindActionToPromise } from 'utils/sagaApiGeneratorHelpers';

import styles from './addFavoritesDropdown.module.scss';

type AddFavoritesDropdownProps = {
  onOpenChange?: (isOpen: boolean) => void;
};

export const AddFavoritesDropdown = ({
  onOpenChange,
}: AddFavoritesDropdownProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const favouriteTeamMemberIds = useAppSelector(
    (state) => state.settings.personal.favouriteTeamMemberIds,
  );

  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const hasOpenedRef = useRef(false);

  const {
    items: agents,
    asyncStatus,
    changeArg,
    search,
    nextPage,
    hasNextPage,
  } = useLazyPaginatedEndpoint({
    endpoint: searchApi.endpoints.readSearchUser,
    itemsSelector: ({ data }) => data.results.hits,
    infinite: true,
  });

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen(open);

      if (open && !hasOpenedRef.current) {
        hasOpenedRef.current = true;
        changeArg({
          limit: 50,
          offset: 0,
          sort: { key: 'display_name', direction: 'asc' },
          filter: { facets: { user_type: ['member'] } },
        });
      }

      if (!open) {
        setSearchQuery('');
      }

      onOpenChange?.(open);
    },
    [changeArg, onOpenChange],
  );

  const toggleFavorite = useCallback(
    async (agentId: string, isFavorite: boolean) => {
      try {
        if (isFavorite) {
          await bindActionToPromise(
            dispatch,
            personalActions.personalRemoveFromFavouriteTeamMemberIds,
          )({ agentId });
        } else {
          await bindActionToPromise(
            dispatch,
            personalActions.personalAddToFavouriteTeamMemberIds,
          )({ agentId });
        }
      } catch {
        // Silently ignore errors
      }
    },
    [dispatch],
  );

  return (
    <div className={styles.addFavoritesButton}>
      <Popover.Root open={isOpen} onOpenChange={handleOpenChange}>
        <Popover.Trigger asChild>
          <Button variant="tertiary" icon="plus">
            Add favorites
          </Button>
        </Popover.Trigger>

        <Popover.Portal>
          <Popover.Content
            className={styles.popoverContent}
            side="right"
            align="center"
          >
            <div className={styles.dropdownScroll}>
              <Text.Paragraph
                variant="3"
                color="tertiary"
                className={styles.dropdownTitle}
              >
                Add to favorites
              </Text.Paragraph>
              <div className={styles.searchInputWrapper}>
                <InputText
                  className={styles.searchInputText}
                  type="search"
                  autoFocus
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    search(e.target.value);
                  }}
                  placeholder="Search agents"
                  suffix={
                    searchQuery
                      ? {
                          type: 'custom',
                          noWrap: true,
                          value: (
                            <IconButton
                              variant="tertiary"
                              icon="close"
                              onClick={() => {
                                setSearchQuery('');
                                search('');
                              }}
                            />
                          ),
                        }
                      : undefined
                  }
                />
              </div>
              <SelectionList isMultiple={false} selectedValues={[]}>
                {agents.map((agent) => {
                  const isFavorite = favouriteTeamMemberIds.includes(agent.id);
                  return (
                    <SelectionList.Item
                      key={agent.id}
                      noFixedSelectionStateSpace
                      contentClassName={styles.itemContent}
                      onSelect={() => toggleFavorite(agent.id, isFavorite)}
                    >
                      <Avatar
                        user={transformSearchUserToUser(agent)}
                        size="24"
                      />
                      <span className={styles.agentName}>
                        {agent.display_name}
                      </span>
                      <Icon
                        name={isFavorite ? 'star-full' : 'star'}
                        color={isFavorite ? 'orange.300' : 'neutral.400'}
                        size={20}
                        className={styles.favoriteIcon}
                      />
                    </SelectionList.Item>
                  );
                })}
                {asyncStatus === 'pending' && (
                  <li>
                    <FlexBox justifyContent="center" padding="200">
                      <Spinner size={16} />
                    </FlexBox>
                  </li>
                )}
                {asyncStatus === 'fulfilled' && agents.length === 0 && (
                  <li>
                    <Text.Paragraph
                      color="tertiary"
                      className={styles.emptyState}
                    >
                      No agents found
                    </Text.Paragraph>
                  </li>
                )}
                {asyncStatus === 'fulfilled' && hasNextPage && (
                  <li>
                    <Waypoint onEnter={nextPage} />
                  </li>
                )}
              </SelectionList>
            </div>
            <Popover.Arrow style={{ fill: 'white' }} width={16} height={8} />
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
};
